import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Check, X, Crosshair } from "lucide-react";

// Custom divIcon pin (brand blue) - avoids the classic CRA/webpack broken-marker-icon
// issue that comes from Leaflet's default PNG asset paths.
const PIN_ICON = L.divIcon({
  className: "nova-org-map-pin",
  html: '<svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25c0-8.3-6.7-15-15-15z" fill="#0052FF"/><circle cx="15" cy="15" r="6" fill="white"/></svg>',
  iconSize: [30, 40],
  iconAnchor: [15, 40],
});

// Reuses the exact same Leaflet + OpenStreetMap stack as Service Areas - no new map
// provider, no Geoapify map rendering. "view" mode shows a fixed marker at stored
// coordinates; "identify" mode lets the user click/drag to manually pinpoint the location.
export const OrgLocationMap = ({ lat, lng, mode = "view", onConfirm, onCancel, testId = "org-location-map" }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [draftLatLng, setDraftLatLng] = useState(lat != null && lng != null ? { lat, lng } : null);

  useEffect(() => {
    if (!elRef.current) return;
    const hasCoords = lat != null && lng != null;
    const map = L.map(elRef.current, {
      zoomControl: true,
      attributionControl: true,
      dragging: mode === "identify" || true,
      scrollWheelZoom: mode === "identify",
    }).setView(hasCoords ? [lat, lng] : [39.5, -98.35], hasCoords ? 15 : 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;

    if (hasCoords) {
      const marker = L.marker([lat, lng], { draggable: mode === "identify", icon: PIN_ICON }).addTo(map);
      markerRef.current = marker;
      if (mode === "identify") {
        marker.on("dragend", () => {
          const pos = marker.getLatLng();
          setDraftLatLng({ lat: pos.lat, lng: pos.lng });
        });
      }
    }

    if (mode === "identify") {
      map.on("click", (e) => {
        const { lat: clickLat, lng: clickLng } = e.latlng;
        if (markerRef.current) {
          markerRef.current.setLatLng(e.latlng);
        } else {
          const marker = L.marker(e.latlng, { draggable: true, icon: PIN_ICON }).addTo(map);
          marker.on("dragend", () => {
            const pos = marker.getLatLng();
            setDraftLatLng({ lat: pos.lat, lng: pos.lng });
          });
          markerRef.current = marker;
        }
        setDraftLatLng({ lat: clickLat, lng: clickLng });
      });
    }

    const t = setTimeout(() => { try { map.invalidateSize(); } catch { /* ignore */ } }, 0);
    return () => {
      clearTimeout(t);
      try { map.remove(); } catch { /* ignore */ }
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className="relative h-full w-full">
      <div ref={elRef} data-testid={testId} className="h-full w-full rounded-xl border border-slate-200 dark:border-white/10" />
      {mode === "identify" && (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 rounded-b-xl bg-white/95 p-2.5 backdrop-blur dark:bg-[#0B1220]/95">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
            <Crosshair size={13} /> Click or drag the pin to set the exact location
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              data-testid={`${testId}-cancel-btn`}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
            >
              <X size={13} /> Cancel
            </button>
            <button
              type="button"
              onClick={() => draftLatLng && onConfirm(draftLatLng.lat, draftLatLng.lng)}
              disabled={!draftLatLng}
              data-testid={`${testId}-confirm-btn`}
              className="inline-flex items-center gap-1 rounded-lg bg-[#0052FF] px-2.5 py-1.5 text-xs font-bold text-white disabled:opacity-50"
            >
              <Check size={13} /> Confirm Location
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
